import config from '@/config';
import { z } from 'zod';
import { StringBooleanSchema, StringNumberSchema } from './primitivesSchema';
import { toArray, validateRange, validateRangeArray, hasNoDuplicates, removeDefaultValue } from './schemaUtils';

export const FilterModeSchema = z.enum(['rental', 'sale']);
export const SortOrderSchema = z.enum(['desc', 'asc']);
export const TypeSchema = z
  .union([StringNumberSchema, StringNumberSchema.array()])
  .refine((val) => {
    if (!val) return true;
    return toArray(val)?.every((v) => Object.keys(config.LISTING_TYPES).includes(v.toString()));
  }, 'Invalid type value')
  .transform((val) => {
    return toArray(val);
  });
export const NumberOfRoomsSchema = z
  .union([StringNumberSchema, StringNumberSchema.array()])
  .refine(validateRangeArray(1, 5), 'numberOfRooms not within range 1-5')
  .refine((val) => (Array.isArray(val) ? hasNoDuplicates(val) : true), {
    message: 'numberOfRooms contains duplicate values',
  })
  .transform((val) => {
    return toArray(val);
  });
export const FeatureGroupSchema = z.enum([
  'apartment',
  'rentalApartment',
  'newHousing',
  'vacationHousing',
  'site',
  'farm',
  'office',
  'storageAndGarage',
]);
export const ExtraFeatureSchema = z.enum(['elevator', 'sauna', 'balcony', 'shore', 'sharedShore']);
export const OrderBySchema = z.enum(['created', 'price', 'rent', 'livingArea', 'totalArea', 'siteArea']);
enum Condition {
  New = 119,
  Good = 120,
  Fair = 121,
  Poor = 122,
}
export const ConditionSchema = z.coerce.number().refine((val) => Object.values(Condition).includes(val), {
  message: 'Invalid condition value',
});
enum ShowingIn {
  All = 0,
  Near = 3,
  Far = 7,
}
export const ShowingInSchema = z.coerce
  .number()
  .refine((val) => Object.values(ShowingIn).includes(val), {
    message: 'Invalid showingIn value',
  })
  .transform(removeDefaultValue(ShowingIn.All));
export const SiteOwnershipSchema = z.enum(['o', 'v']);
export const PostalCodeSchema = z.string().refine((val) => /^[0-9]{5}$/.test(val));
export const StreetIdSchema = z.string().refine((val) => /^[0-9a-z]{40}$/.test(val));
export const EnergyClassificationSchema = z.enum(['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']);

export const FiltersSchema = z
  .object({
    mode: FilterModeSchema.optional(),
    featureGroup: FeatureGroupSchema.optional(),
    companyId: StringNumberSchema.optional(),
    // Location filters
    cityId: z
      .union([StringNumberSchema, StringNumberSchema.array()])
      .optional()
      .transform((val) => {
        return toArray(val);
      }),
    postalCode: z
      .union([PostalCodeSchema, PostalCodeSchema.array()])
      .optional()
      .transform((val) => {
        return toArray(val);
      }),
    countyId: z
      .union([StringNumberSchema, StringNumberSchema.array()])
      .optional()
      .transform((val) => {
        return toArray(val);
      }),
    districtId: z
      .union([StringNumberSchema, StringNumberSchema.array()])
      .optional()
      .transform((val) => {
        return toArray(val);
      }),
    regionId: z
      .union([StringNumberSchema, StringNumberSchema.array()])
      .optional()
      .transform((val) => {
        return toArray(val);
      }),
    streetId: z
      .union([StreetIdSchema, StreetIdSchema.array()])
      .optional()
      .transform((val) => {
        return toArray(val);
      }),
    priceMin: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.PRICE_MIN, config.FILTER_LIMITS.PRICE_MAX),
      'priceMin not within range'
    )
      .transform(removeDefaultValue(config.FILTER_LIMITS.PRICE_MIN))
      .optional(),
    priceMax: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.PRICE_MIN, config.FILTER_LIMITS.PRICE_MAX),
      'priceMax not within range'
    )
      .transform(removeDefaultValue(config.FILTER_LIMITS.PRICE_MAX))
      .optional(),
    order: SortOrderSchema.optional(),
    orderBy: OrderBySchema.optional(),
    numberOfRooms: NumberOfRoomsSchema.optional(),
    type: TypeSchema.optional(),
    siteOwnership: SiteOwnershipSchema.optional(),
    livingAreaMin: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.AREA_MIN, config.FILTER_LIMITS.AREA_MAX),
      'livingAreaMin not within range'
    ).transform(removeDefaultValue(config.FILTER_LIMITS.AREA_MIN)),
    livingAreaMax: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.AREA_MIN, config.FILTER_LIMITS.AREA_MAX),
      'livingAreaMax not within range'
    ).transform(removeDefaultValue(config.FILTER_LIMITS.AREA_MAX)),
    totalAreaMin: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.TOTAL_AREA_MIN, config.FILTER_LIMITS.TOTAL_AREA_MAX),
      'totalAreaMin not within range'
    ).transform(removeDefaultValue(config.FILTER_LIMITS.TOTAL_AREA_MIN)),
    totalAreaMax: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.TOTAL_AREA_MIN, config.FILTER_LIMITS.TOTAL_AREA_MAX),
      'totalAreaMax not within range'
    ).transform(removeDefaultValue(config.FILTER_LIMITS.TOTAL_AREA_MAX)),
    siteAreaMin: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.SITE_AREA_MIN, config.FILTER_LIMITS.SITE_AREA_MAX),
      'siteAreaMin not within range'
    ).transform(removeDefaultValue(config.FILTER_LIMITS.SITE_AREA_MIN)),
    siteAreaMax: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.SITE_AREA_MIN, config.FILTER_LIMITS.SITE_AREA_MAX),
      'siteAreaMax not within range'
    ).transform(removeDefaultValue(config.FILTER_LIMITS.SITE_AREA_MAX)),
    condition: z
      .union([ConditionSchema, ConditionSchema.array()])
      .optional()
      .transform((val) => {
        return toArray(val);
      }),
    feature: z
      .union([ExtraFeatureSchema, ExtraFeatureSchema.array()])
      .optional()
      .transform((val) => {
        return toArray(val);
      }),
    yearMin: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.YEAR_MIN, config.FILTER_LIMITS.YEAR_MAX),
      'yearMin not within range'
    ).transform(removeDefaultValue(config.FILTER_LIMITS.YEAR_MIN)),
    yearMax: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.YEAR_MIN, config.FILTER_LIMITS.YEAR_MAX),
      'yearMax not within range'
    ).transform(removeDefaultValue(config.FILTER_LIMITS.YEAR_MAX)),
    rentMin: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.RENT_MIN, config.FILTER_LIMITS.RENT_MAX),
      'rentMin not within range'
    ).transform(removeDefaultValue(config.FILTER_LIMITS.RENT_MIN)),
    rentMax: StringNumberSchema.refine(
      validateRange(config.FILTER_LIMITS.RENT_MIN, config.FILTER_LIMITS.RENT_MAX),
      'rentMax not within range'
    ).transform(removeDefaultValue(config.FILTER_LIMITS.RENT_MAX)),
    showingIn: ShowingInSchema.optional(),
    agentId: StringNumberSchema.optional(),
    has360: StringBooleanSchema.optional(),
    energy: z
      .union([EnergyClassificationSchema, EnergyClassificationSchema.array()])
      .optional()
      .transform((val) => {
        return toArray(val);
      }),
  })
  .strict();
